import { useState, useEffect } from "react";
// Types
import { Device, FlashMode, defaultProfile, Profile, emptyMode, ProfileMode, FirmwareRecord } from "../../Types";
// Context
import { useGlobalContext } from "../../Context/GlobalContext";
import { useDeviceContext } from "../../Context/DeviceContext";
// Mui
import { Grid2 as Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
// Components
import Button from "../../Components/button";
import Product from "../../Components/product";
import Loading from "../../Components/loading";
import Select from "../../Components/select";
import Toggle from "../../Components/toggle";
// Partials
import { DeviceModes, ModesList } from "../../Partials/modes";
import Profiles from "../../Partials/profiles";
import FlashModeSettings from "../../Partials/flashSettings";
import CobberSettings from "../../Partials/cobberSettings";
import { EbikeInfo, Ebike } from "../../Partials/ebike";
// Utils
import Notify from "../../Utils/Notify";
import { useWindowSize } from "../../Utils/Window";
// Lib
import { 
    isCartesian, 
    hasCobberSettings, 
    needsPWMCheck, 
    unifiedModes,
    compatibleEbike,
} from '../../Lib/Devices';
import { isEqual } from "../../Utils/Json";

const EditDevice = ({ deviceID, setView }: { deviceID: number, setView: Function }) => {

    const { 
        deviceManager, 
        usbManager,
        dbManager, 
        showPopup, 
        hidePopup
    } = useGlobalContext();

    const {
        setCartesianPatterns,
        patterns,
        setPatterns
    } = useDeviceContext();

    const { width } = useWindowSize();

    const [modesAvailable, setModesAvailable] = useState<FlashMode[]>([]);
    const [steadyModesActive, setSteadyModesActive] = useState<FlashMode[]>([]);
    const [flashModesActive, setFlashModesActive] = useState<FlashMode[]>([]);
    const [device, setDevice] = useState<Device | null>(null);
    const [currentProfile, setCurrentProfile] = useState<number>(-1);
    const [hasNewFirmware, setHasNewFirmware] = useState(false);
    const [firmwareName, setFirmwareName] = useState('');
    const [showLoadingScreen, setShowLoadingScreen] = useState(false);
    const [hasEbike, setHasEbike] = useState(false);
    const [status, setStatus] = useState("");
    const [pendingUpdate, setPendingUpdate] = useState(false);
    const [uploadProgess, setUploadProgress] = useState(0);
    const [loadingIsDeterminant, setLoadingIsDeterminant] = useState(false);
    const [powerOptions, setPowerOptions] = useState([]);
    const [make, setMake] = useState('-');
    const [model, setModel] = useState('-');
    const [voltage, setVoltage] = useState(0);
    const [wattage, setWattage] = useState(0);
    const [PWMRange, setPWMRange] = useState({ min: 0, max: 100 });
    const [allMakes, setAllMakes] = useState<any[]>([]);
    const [voltageOptions, setVoltageOptions] = useState<any[]>([]);
    const [wattageOptions, setWattageOptions] = useState<any[]>([]);
    const [initialized, setInitialized] = useState<boolean>(false);
    const [availableFirmware, setAvailableFirmware] = useState<FirmwareRecord[]>([]);
    const [lastConfigured, setLastConfigured] = useState<string>('');
    const [profileNameEdit, setProfileNameEdit] = useState<boolean>(false);

    useEffect(() => {
        const init = async () => {
            let device = await deviceManager.findDevice(deviceID);
            if(!device) return;

            const allMakes = await dbManager.request('/items/bike_makes');
            setAllMakes([...(allMakes), {id: 0, name: 'Other'} ]);
            setPowerOptions(await dbManager.request('/items/bike_power'));

            const options = await dbManager.request('/items/options');
            setVoltageOptions(options.other_ebike_voltages.map((item: any) => ({key: `${item}V`, value: item.toString()})));
            setWattageOptions(options.other_ebike_powers.map((item: any) => ({key: `${item}W`, value: item.toString()})));

            setPatterns(await dbManager.request('/items/patterns'));

            setCartesianPatterns(isCartesian(device.type.id));

            const allFirmware = await dbManager.request('/items/firmware');
            const firmware_number = await usbManager.readFirmwareVersion(device.type.chip_id);
            const firmware = allFirmware.find((f: any) => f.version_number === firmware_number && f.device_type === device?.type.id);
            if(firmware) device.firmware = firmware.id;
            setAvailableFirmware(allFirmware);

            // Set the new firmware
            const newFirmware: FirmwareRecord = await deviceManager.checkForNewFirmware(device, allFirmware);
            if(newFirmware) {
                setHasNewFirmware(true);
                setTimeout(() => {
                    handleFirmwareUpdate(device!, newFirmware);
                }, 1000);
            } else {
                verifyFirmware(device, allFirmware);
            }

            setDevice(device);
            setCurrentProfile(device.properties?.uploadedProfile as number ?? device.profiles[0].id);
            setInitialized(true);
            // showOnboarding();
        }
        if(!initialized) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceID, deviceManager]);


    // When profile changes
    useEffect(() => {
        const syncDevice = async () => {
            // Get the device from the list
            if(!device) return;            
                
                if(device.firmware) setFirmwareName(availableFirmware?.find((f: any) => f.id === device?.firmware)?.name ?? "Unkown");

                const selectedProfile: Profile | undefined = device.profiles.find((profile) => profile.id === currentProfile);
                if(!selectedProfile) return;

                device.profiles.sort((a, b) => {
                    if (a.id === currentProfile) return -1;
                    if (b.id === currentProfile) return 1;
                    return 0;
                });

                // Get the active steady modes for the current profile
                const activeSteadyModes: FlashMode[] | undefined = [];
                selectedProfile.steady_modes.forEach((mode: ProfileMode, index: number) => {
                    const flashMode = device?.availableModes?.find((availableMode: FlashMode) => availableMode.id === mode.id);
                    if (flashMode) activeSteadyModes.push({
                        ...flashMode,
                        config: mode.config,
                        settings: flashMode.settings,
                        instance: mode.instance || `steady-${mode.id}-${index}`,
                    });
                });

                // Set the active steady modes
                setSteadyModesActive(activeSteadyModes ?? []);

                // Get the active flash modes for the current profile
                const activeFlashModes: FlashMode[] | undefined = [];
                
                selectedProfile.flash_modes.forEach((mode: ProfileMode, index: number) => {
                    const flashMode = device?.availableModes?.find((availableMode: FlashMode) => availableMode.id === mode.id);
                    if (flashMode) activeFlashModes.push({
                        ...flashMode,
                        config: mode.config,
                        settings: flashMode.settings,
                        instance: mode.instance || `flash-${mode.id}-${index}`,
                    });
                });

                // Set the active flash modes
                setFlashModesActive(activeFlashModes ?? []);

                // Check if has ebike
                await setEbikeOptions(selectedProfile.settings.ebike_power_id?.toString(), device);

                // Set all available modes
                checkModesAvailable([...(activeSteadyModes ?? []), ...(activeFlashModes ?? [])]);

                if(profileNameEdit) {
                    handleEditProfile(currentProfile)
                    setProfileNameEdit(false);
                }
        }
        if(currentProfile > -1) syncDevice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProfile, pendingUpdate]);

    // Update Steady Modes
    useEffect(() => {
        const updateSteadyModes = async () => {
            if (!device || !findProfile()) return;
            
            const newSteadyModes = steadyModesActive.map((mode) => ({
                id: mode.id,
                config: mode.config,
                instance: mode.instance,
            }));

            const updatedProfiles = device!.profiles.map((profile) =>
                profile.id === findProfile()?.id
                    ? { ...profile, steady_modes: newSteadyModes }
                    : profile
            );


            if(!isEqual(device.profiles, updatedProfiles)) {
                updateDevice({ profiles: updatedProfiles });
            }

            checkModesAvailable([...(steadyModesActive ?? []), ...(flashModesActive ?? [])]);
        };

        updateSteadyModes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steadyModesActive]);

    // Update Flash Modes
    useEffect(() => {
        const updateFlashModes = async () => {
            if (!device || !findProfile()) return;

            const newFlashModes = flashModesActive.map((mode) => {
                return {
                    id: mode.id,
                    config: {
                        ...mode.config,
                        brakeMode: mode.config?.brakeMode ? mode.config?.brakeMode : mode.default_pattern
                    },
                    instance: mode.instance,
                }
            });

            const updatedProfiles = device.profiles.map((profile) =>
                profile.id === findProfile()?.id
                    ? { ...profile, flash_modes: newFlashModes }
                    : profile
            );

            if(!isEqual(device.profiles, updatedProfiles)) {
                updateDevice({ profiles: updatedProfiles });
            }

            checkModesAvailable([...(steadyModesActive ?? []), ...(flashModesActive ?? [])]);
        };

        updateFlashModes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flashModesActive]);

    // Util Functions
    const verifyFirmware = async(device: Device, allFirmware: FirmwareRecord[]) => {
        if(!device.firmware) return;
        
        const firmwareRecord: FirmwareRecord | undefined = allFirmware.find((f: any) => f.id === device.firmware);

        if(!firmwareRecord) return;
        const payload: any = await dbManager.getFirmwarePayload(firmwareRecord); 

        if(!(await usbManager.integrityCheck(await usbManager.parseFirmware(payload, device.type.chip_id)))) {
            showPopup({
                title: "CORRUPTED FIRMWARE",
                content: "YOUR DEVICE CONTAINS INVALID FIRMWARE, PLEASE RE-UPLOAD OR RETURN TO DEVICE LIST",
                confirmText: "UPLOAD",
                cancelText: "RETURN TO LIST",
                onConfirm: async () => {
                    displayLoading(
                        "GETTING LATEST FIRMWARE",
                        async () => {
                            if(!(await updateFirmware(device, firmwareRecord))) verifyFirmware(device, allFirmware);
                        }
                    );
                }, 
                onCancel: () => setView('deviceList')
            })
        }
    }

    const checkPWM = (mode: FlashMode) => {
        return mode.flash_pattern.filter(({ command }) => command === "PWM")
        .reduce((maxPWM, { data }) => Math.max(maxPWM, data as number), 0) <= PWMRange.max
    }

    const findProfile = (id: number = currentProfile) => {
        return device?.profiles.find((profile) => profile.id === id);
    }

    const patchCurrentProfile = async (data: any) => {
        updateDevice({ 
            profiles: device!.profiles.map((profile) => {
                if(profile.id === currentProfile) {
                    return { ...profile, ...data };
                }
                return profile;
            })
        });
    }

    const getFlashModes = (modes: ProfileMode[]): FlashMode[] => {
        return modes.map((mode: ProfileMode) => {
            const flashMode = device?.availableModes?.find((availableMode) => availableMode.id === mode.id);
            if (!flashMode) return emptyMode;
            return {
                id: flashMode.id,
                name: flashMode.name,
                mode_type: flashMode.mode_type,
                flash_pattern: flashMode.flash_pattern,
                eye_saver_pattern: flashMode.eye_saver_pattern,
                allow_custom: flashMode.allow_custom,
                settings: flashMode.settings,
                config: mode.config,
                brake_patterns: flashMode.brake_patterns ?? [],
                instance: mode.instance
            }
        });
    }

    const setEbikeOptions = async (ebikePowerID: string, device: Device) => {
        const hasEbike = !!ebikePowerID;
        setHasEbike(hasEbike);
        
        if(!hasEbike) {
            setMake('-');
            setModel('-');
            setVoltage(0);
            setWattage(0);
            setPWMRange({ min: 0, max: 100 });
        } else {
            const isCustom = ebikePowerID === '0';
            const allMakes = await dbManager.request('/items/bike_makes');
            const powerOption = (await dbManager.request(`/items/bike_power?filter[identifier][_eq]=${ebikePowerID}`))[0];
    
            setMake(isCustom ? 'Other' : allMakes.find((make: any) => make.id === powerOption.make)?.name);
            setModel(isCustom ? 'Other' : powerOption.model);
    
            const voltage = isCustom ? parseFloat(findProfile()?.settings.customVoltage) ?? 0 : powerOption.voltage
            setVoltage(parseFloat(voltage));
    
            const power = isCustom ? parseFloat(findProfile()?.settings.customPower) ?? 0 : powerOption.power
            setWattage(parseFloat(power));
    
            const maxValues = device.type.power.max[`${voltage > 12 ? 12 : 6}v` as "6v" | "12v"];
            let powerSetting: any = maxValues?.find((setting: any) => setting.power === power);
            if(!powerSetting) powerSetting = power < maxValues[0].power ? maxValues[0] : maxValues[maxValues.length - 1];
    
            if(powerSetting) setPWMRange({ min: device.type.power.min, max: powerSetting.value });
        }

        setTimeout(() => {
            if(status === 'SAVING E-BIKE SETTINGS') setShowLoadingScreen(false);
        }, 1000);
    }

    const checkModesAvailable = (activeFlashModes: FlashMode[]) => {
        // Get modes that fit the PWM range (if e-bike is enabled)
        // Note: Modes can be added multiple times, so we don't filter by activeFlashModes anymore
        const currModesAvailable = device?.availableModes?.filter(
            (mode) => {
                // No need to check PWM for cobber
                return !needsPWMCheck(device.type.id) || !hasEbike || checkPWM(mode);
            }
        );

        // Sort the modes based on the order
        const sortedModes = currModesAvailable?.sort((a, b) => {
            return (a?.order ?? 0) - (b?.order ?? 0);
        });
        setModesAvailable(sortedModes ?? []);
    }

    const displayLoading = async (status: string, loadingFunction?: Function, noStop?: boolean) => {
        setStatus(status);
        setShowLoadingScreen(true);
        if(loadingFunction) {
            await loadingFunction();
            if(!noStop) setShowLoadingScreen(false);
        }
    }

    const updateDevice = (data: any, deviceItem?: Device, synced: boolean | null = null) => {
        // If no device is passed, use the local device
        if(!deviceItem) deviceItem = device!;
        // If no device is found return
        else if(!deviceItem && !device) return;

        const newDevice: Device = {
            ...deviceItem,
            ...data
        }
        newDevice.properties!.synced = synced !== null ? synced : isEqual(newDevice, device)

        // Local instance of the device
        setDevice(newDevice);

        // Update the device in the database
        deviceManager.updateDevice(
            deviceItem.id,
            {
                firmware: newDevice.firmware,
                profiles: newDevice.profiles,
                properties: newDevice.properties,
                type: newDevice.type.id,
                name: newDevice.name,
                hardware_id: newDevice.hardwareId,
            }
        );
    }

    const getFlashmodeMaxBrightness = (mode: FlashMode) => {
        let maxBrightness = 0;
        mode.flash_pattern.forEach((pattern) => {
            if (pattern.command === 'PWM') {
                if (pattern.data as number > maxBrightness) maxBrightness = pattern.data as number;
            }
        });
        return maxBrightness;
    }

    const generateInstanceId = () => {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    // Mode Handlers
    const handleModeDrop = (mode: FlashMode, toActive: boolean = true) => { 
        if(mode.mode_type === "steady") {
            setSteadyModesActive((prevSteadyModesActive) => {
                if(toActive) {
                    const newList = [...prevSteadyModesActive];
                    // Add unique instance ID to track this specific mode instance
                    const modeWithInstance = { ...mode, instance: generateInstanceId() };
                    // Loop through the modes to check the max brightness and add the mode in the correct position
                    let maxBrightness = needsPWMCheck(device?.type.id ?? 0) ? getFlashmodeMaxBrightness(modeWithInstance) : 100;
                    let i = 0;
                    while(i < newList.length && getFlashmodeMaxBrightness(newList[i]) > maxBrightness) i++;
                    newList.splice(i, 0, modeWithInstance);
                    return newList;
                } else {
                    // Remove only the specific instance, not all modes with this ID
                    return prevSteadyModesActive.filter((m) => m.instance !== mode.instance);
                }
            });
        } else {
            setFlashModesActive((prevFlashModesActive) => {
                if(toActive) {
                    // Add unique instance ID to track this specific mode instance
                    const modeWithInstance = { ...mode, instance: generateInstanceId() };
                    return [...prevFlashModesActive, modeWithInstance];
                } else {
                    // Remove only the specific instance, not all modes with this ID
                    return prevFlashModesActive.filter((m) => m.instance !== mode.instance);
                }
            });                
        }
    };

    const handleCannotDrop = (isSteady: boolean) => {
        Notify(
            "error", 
            isSteady ? 
            "You can select up to four flash modes (only) on this row.": 
            "You can select up to four steady modes (only) on this row."
        );
    }

    const handleModeUpdate = (mode: FlashMode) => {
        setLastConfigured(mode.instance ?? '')
        if(mode.mode_type === "steady" && !unifiedModes(device?.type.id ?? 0)) {
            setSteadyModesActive((prevModesActive) => {
                const newModes = prevModesActive.map((m) => {
                    if (m.instance === mode.instance) {
                        return {
                            ...m,
                            config: {   
                               ...mode.config
                            }
                        }
                    }
                    return m;
                });
                return newModes;
            });
        } else {
            setFlashModesActive((prevModesActive) => {
                const newModes = prevModesActive.map((m) => {
                    if (m.instance === mode.instance) {
                        return {
                            ...m,
                            config: {   
                               ...mode.config
                            }
                        }
                    }
                    return m;
                });
                return newModes;
            });
        }
    }

    const handleModeClick = (mode: FlashMode, active: Boolean) => {
        if (active) showPopup({
            title: mode.name,
            content: <FlashModeSettings 
                mode={{...mode, ...(!mode.config ? {config: {}} : {})}} 
                handleModeUpdate={handleModeUpdate} 
                type={device!.type}
                handleRemove={() => handleModeDrop(mode, false)}
            />,
            customClass: "small-padding no-buttons",
        });
    };
    
    // Profile Handlers
    const handleAddProfile = async () => {
        if(!device) return;

        displayLoading(
            "CREATING NEW PROFILE",
            async () => {
                let highestNum: any = -1;
                device.profiles.forEach((profile) => {
                    const match = profile.name.match(/^Default(?: (\d+))?$/);;
                    if (match) {
                        const num = parseInt(match[1] ?? '0');
                        if (num > highestNum) {
                            highestNum = num;
                        }
                    }
                });

                const profileToAdd: Profile = {
                    ...defaultProfile, 
                    settings: {
                        accel_threshold: device?.type.sensitivities?.accel_range[device?.type.sensitivities.default],
                        sleep_threshold: device?.type.sensitivities?.sleep_range[device?.type.sensitivities.default],
                        smartLighting: device?.type.default_smart_lighting,
                        min_time_before_wake: device?.type.default_wake,
                        min_time_before_sleep: device?.type.default_sleep,
                    },
                    name: `Default${highestNum > -1 ? ' ' + (parseInt(highestNum) + 1) : ''}`, 
                    steady_modes: [],
                    flash_modes: [],
                };

                const newProfile: Profile = { 
                    ...profileToAdd, 
                    id:  device!.profiles.length > 0 ? Math.max(...device!.profiles.map((profile) => profile.id)) + 1 : 1
                };

                updateDevice({ profiles: [...device.profiles, newProfile] });

                setTimeout(() => {
                    setShowLoadingScreen(false);
                    setCurrentProfile(newProfile.id);
                    setProfileNameEdit(true)
                }, 1000);
            },
            true
        );
    }

    const handleProfileSwitch = (id: number) => {
        if(id === currentProfile) return;
        else setCurrentProfile(id);
    };

    const handleUpdateSettings = async (data: {[key: string] : any}) => {
        await patchCurrentProfile({
            settings: { ...findProfile()?.settings, ...data }
        });
    }

    const handleUploadProfile = async () => {
        displayLoading(
            "UPLOADING PROFILE",
            async () => {          
                const profileToUpload = findProfile(currentProfile);
                if(!profileToUpload) return;

                let modesToWrite = [];
                if(!unifiedModes(device?.type.id ?? 0)) {
                    modesToWrite.push(...getFlashModes(profileToUpload.steady_modes));
                    // pad the modes to 4
                    for (let i = modesToWrite.length; i < 4; i++) {
                        modesToWrite.push(emptyMode);
                    }
                }
                modesToWrite.push(...getFlashModes(profileToUpload.flash_modes));
                // pad the modes to 8
                for (let i = modesToWrite.length; i < 8; i++) {
                    modesToWrite.push(emptyMode);
                }

                if(!modesToWrite.find((m: FlashMode) => m.id > 0)) {
                    Notify('error', 'Please upload at least 1 mode');
                    return;
                }

                updateDevice({
                    properties: { 
                        ...device!.properties, 
                        uploadedProfile: currentProfile,
                    }
                }, undefined, true);  

                // Inject the reflex pattern
                modesToWrite = modesToWrite.map((mode: any) => {
                    if(!mode?.config?.brakeMode) return mode;

                    const brakePattern = patterns.find((pattern: any) => pattern.id === mode.config.brakeMode);

                    return {
                        ...mode,
                        flash_pattern: {
                            ...mode.flash_pattern,
                            z_patterns: [brakePattern?.pattern ?? []],
                            setup: {
                                ...mode.flash_pattern?.setup,
                                z: brakePattern?.setup,
                            },
                        },
                    };
                });

                if(!(await usbManager.writeData(
                    modesToWrite,
                    {
                        min_time_before_sleep: profileToUpload.settings?.smartLighting ? (profileToUpload.settings?.min_time_before_sleep ?? 0) / 10 : 0,
                        min_time_before_wake: profileToUpload.settings?.min_time_before_wake ?? 0,
                        accel_threshold: findProfile()?.settings.accel_threshold ?? 0,
                        sleep_threshold: findProfile()?.settings.sleep_threshold ?? 0,
                        ...device?.type.custom_memory,
                    }
                ))) {
                    Notify('error', 'Something went wrong, please try again');
                    return;
                }    

                showPopup({
                    title: "UPLOAD SUCCESS",
                    content: <p style={{padding: '20px 10px'}}>
                        PROFILE '{profileToUpload.name}' HAS BEEN UPLOADED SUCCESSFULLY
                        <br/><br/>
                        IT IS SAFE TO UNPLUG YOUR LIGHT
                    </p>,
                    confirmText: "OK",
                    customClass: "button-small"
                });    
            }
        );
    }

    const handleEditProfile = (profileId: number) => {
        const selectedProfile = findProfile(profileId); 
        if(!selectedProfile) return;
        showPopup({
            title: "EDIT PROFILE",
            content: (
                <div>
                    <p>EDIT PROFILE NAME</p>
                    <input
                        type="text"
                        placeholder="Device Name"
                        defaultValue={selectedProfile.name}
                        onChange={(e) => selectedProfile.name = e.target.value}
                    />
                </div>
            ),
            onConfirm: async () => {
                const newProfiles = device!.profiles.map((profile) => {
                    if (profile.id === profileId) {
                        return { ...profile, name: selectedProfile.name };
                    }
                    return profile;
                });

                updateDevice({ profiles: newProfiles });
            },
            confirmText: "SAVE",
            customClass: "button-small"
        });
    }

    const handleDeleteProfile = () => {

        if(device!.profiles.length === 1) {
            Notify("error", "You must have at least one profile");
            return;
        }

        showPopup({
            title: "DELETE PROFILE",
            content: (
                <p>
                    ARE YOU SURE YOU WOULD LIKE TO REMOVE PROFILE
                    <span style={{ fontWeight: '700' }}> 
                        '{findProfile(currentProfile)?.name?.toUpperCase()}'
                    </span>
                </p>
            ),
            onConfirm: async () => {
                displayLoading(
                    "DELETING PROFILE",
                    async () => {             
                        const newProfiles = device!.profiles.filter((profile) => profile.id !== currentProfile);
                        updateDevice({ 
                            profiles: newProfiles
                        });
                        setTimeout(() => {
                            setCurrentProfile(newProfiles[0].id);
                            setShowLoadingScreen(false);
                        }, 1000);
                    },
                    true
                );
            },
            confirmText: "YES",
            cancelText: "NO",
            customClass: "button-small"
        });
    }

    const handleResetProfile = () => {
        showPopup({
            title: "RESET PROFILE",
            content: (
                <p>
                    ARE YOU SURE YOU WOULD LIKE TO RESET PROFILE
                    <span style={{ fontWeight: '700' }}> 
                        '{findProfile(currentProfile)?.name?.toUpperCase()}'
                    </span>
                </p>
            ),
            onConfirm: async () => {
                displayLoading(
                    "RESETTING PROFILE",
                    async () => {
                        if(!device) return;
                        const newProfiles = device!.profiles.map((profile) => {
                            if (profile.id === currentProfile) {
                                const flashModes: FlashMode[] = device?.availableModes?.filter((m: FlashMode) => m.default) ?? [];                         
                                return { 
                                    ...defaultProfile,
                                    flash_modes: unifiedModes(device.type.id) ? flashModes.sort((a: FlashMode, b: FlashMode) => (a.order ?? 0) - (b.order ?? 0)) : [],
                                    settings: {                        
                                        "accel_threshold": device.type.sensitivities.accel_range[device?.type.sensitivities.default],
                                        "sleep_threshold": device.type.sensitivities.sleep_range[device?.type.sensitivities.default],
                                        min_time_before_sleep: device.type.default_sleep,
                                        min_time_before_wake: device.type.default_wake,
                                        smartLighting: device.type.default_smart_lighting,
                                    }, 
                                    id: profile.id 
                                };
                            }
                            
                            return profile;
                        });

                        updateDevice({ profiles: newProfiles });

                        setTimeout(() => {
                            setPendingUpdate(!pendingUpdate);
                            setCurrentProfile(currentProfile);
                            setShowLoadingScreen(false);
                        }, 1000);
                    },
                    true
                );
            },
            confirmText: "YES",
            cancelText: "NO",
            customClass: "button-small"
        });

    }

    // Firmware Update
    const handleFirmwareUpdate = async (device: Device, firmware: FirmwareRecord) => {
        if(!firmware) return;
        showPopup({
            title: "UPDATE FIRMWARE",
            content: (
                <p style={{width: '75%', margin: '20px auto'}}>
                    THERE'S A FIRMWARE UPDATE AVAILABLE FOR THIS LIGHT. WOULD YOU LIKE TO INSTALL IT?
                </p>
            ),
            footerContent: (
                <p style={{fontSize: '10px', marginBottom: '-5px', marginTop: '20px'}}>
                    NOTE: FIRMWARE UPDATES CAN IMPROVE SOFTWARE AND / OR PERFORMANCE. 
                    PLEASE NOTE UPDATES WILL ALSO RESET YOUR LIGHT TO DEFAULT SETTINGS.
                </p>
            ),
            onConfirm: async () => {
                displayLoading(
                    "GETTING LATEST FIRMWARE",
                    async () => await updateFirmware(device, firmware)
                );
            }, 
            confirmText: "YES",
            cancelText: "MAYBE LATER",
        });
    }

    const updateFirmware = async (device: Device, firmwareRecord: FirmwareRecord) => {
        const firmware: any = await usbManager.parseFirmware(await dbManager.getFirmwarePayload(firmwareRecord), device.type.chip_id);
        setLoadingIsDeterminant(true);

        if(firmware === false) {
            Notify("error", "Error reading firmware file");
            return;
        }

        if(!firmware) {
            Notify("error", "This firmware is not compatible with this device");
            return;
        }
        
        setStatus("UPLOADING FIRMWARE TO DEVICE");
        if(await usbManager.updateFirmware(firmware, setUploadProgress)) {
            setLoadingIsDeterminant(false);
            setStatus("SAVING DEVICE");
            setFirmwareName(firmwareRecord.name)
            updateDevice({ firmware: firmwareRecord.id }, device);
            setHasNewFirmware(false);
            showPopup({
                title: "UPDATE COMPLETE",
                content: (
                    <p>
                        YOUR LIGHT HAS BEEN UPDATED TO THE LATEST FIRMWARE VERSION
                        <span style={{ fontWeight: '700' }}> {firmwareRecord.name} </span>
                    </p>
                ),
                confirmText: "OK",
                customClass: "button-small"
            });
            return true;
        } else {
            Notify('error', 'Error Uploading firmware, please try again');
            return false;
        }
    }

    // Device Handlers
    const handleDelete = () => {
        showPopup({
            title: "DELETE LIGHT",
            content: (
                <p>
                    ARE YOU SURE YOU WOULD LIKE TO REMOVE
                    <span style={{ fontWeight: '700' }}> '{device!.name?.toUpperCase()}' </span>
                    FROM YOUR LIBRARY?
                </p>
            ),
            onConfirm: async () => {
                displayLoading(
                    "DELETING DEVICE FROM MODEMAKER",
                    async () => {
                        await deviceManager.deleteDevice(device!.id);
                        setView('deviceList');
                    }
                );
            },
            confirmText: "YES",
            cancelText: "NO",
            customClass: "button-small"
        });
    };

    const handleNameChange = () => {
        let newName = device?.name;
        showPopup({
            title: "EDIT DEVICE NAME",
            content:
                <div className="name-change">
                    <p>ENTER NEW DEVICE NAME</p>
                    <input
                        type="text"
                        placeholder="Device Name"
                        defaultValue={device?.name}
                        onChange={(e) => newName = e.target.value}
                    />
                </div>,
            onConfirm: async () => {
                if (newName && newName !== device?.name) {
                    updateDevice({ name: newName });
                }
            },
        });
    };

    // const showOnboarding = () => {
    //     showPopup({
    //         title: "",
    //         content: <video 
    //             width="100%" 
    //             autoPlay 
    //             muted 
    //             onEnded={() => hidePopup()}
    //         >
    //             <source src="/assets/Demo.mp4" type="video/mp4"/>
    //         </video>,
    //         noButtons: true,
    //         customClass: 'large-popup',
    //         closeIcon: true
    //     });
    // };

    const EbikeForm = (
        { onSave }: 
        { onSave: (powerOption: string, voltage?: number, power?: number) => void }
    ) => {
        const [localMake, setLocalMake] = useState(-1);
        const [localPowerOption, setLocalPowerOption] = useState('');
        const [voltage, setVoltage] = useState(0);
        const [power, setPower] = useState(0);

        return (
            <div style={{ padding: '20px 50px' }}>
                <div style={{ marginBottom: '20px' }}>
                    PLEASE ENTER YOUR E-BIKE MOTOR MAKE AND MODEL
                </div>
                <div style={{ marginBottom: '20px' }}>
                    {allMakes.length && <Select
                        options={allMakes.map((item) => ({ key: item.name, value: item.id }))}
                        value={localMake}
                        setValue={setLocalMake}
                    />}
                </div>
                {localMake > 0 && <Select
                    options={
                        powerOptions
                        .filter((option: { make: number; model: string; identifier: string }) => option.make === localMake)
                        .map((item: { model: string; identifier: string }) => ({ key: item.model, value: item.identifier }))
                    }
                    value={localPowerOption}
                    setValue={setLocalPowerOption}
                />}
                {localMake === 0 && 
                    <>
                        <Select
                            value={voltage}
                            setValue={setVoltage}
                            options={voltageOptions}
                            defaultText="Select Voltage"
                        />
                        <div style={{marginTop: '20px'}}></div>
                        <Select
                            value={power}
                            setValue={setPower}
                            options={wattageOptions}
                            defaultText="Select Power"
                        />
                    </>
                }
                <div style={{ marginTop: '20px' }}></div>
                <button 
                    onClick={() => {
                        if(localPowerOption) {
                            onSave(localPowerOption);
                        } else if(localMake === 0) {
                            if(voltage && power) {
                                onSave('0', voltage, power);
                            } else {
                                Notify("error", "Please select a voltage and power");
                            }
                        } else {
                            Notify("error", "Please select a make and model");
                        } 
                    }} 
                    style={{ marginTop: '20px' }}
                >
                    Save
                </button>
            </div> 
        );
    }

    const handleAddEbike = async(toggleValue: boolean) => {
        if(!device) return;
        if(toggleValue) {
            showPopup({
                title: "E-BIKE PWR",
                content: (
                    <EbikeForm 
                        onSave={async (powerOption: string, voltage?: number, power?: number) => {
                            displayLoading(
                                "SAVING E-BIKE SETTINGS",
                                async () => {
                                    hidePopup();
                                    setHasEbike(toggleValue);
                                    await setEbikeOptions(powerOption, device);
                                    await patchCurrentProfile({ 
                                        settings: { 
                                            ...findProfile()?.settings, 
                                            ebike_power_id: powerOption,
                                            ...(powerOption === '0' ? { customVoltage: voltage, customPower: power } : {})
                                        }, 
                                    });
                                    setFlashModesActive([]);
                                    setSteadyModesActive([]);
                                    setTimeout(() => {
                                        setPendingUpdate(!pendingUpdate);
                                    }, 1000);
                                },
                                true
                            )
                        }}
                    />
                ),
                titleIcon: <InfoOutlinedIcon />,
                customClass: "button-small",
                onMouseEnter: () => {
                    showPopup({
                        title: "E-BIKE PWR",
                        content: <EbikeInfo />,
                        titleIcon: <InfoOutlinedIcon />,
                        sub: true,
                        noSubBlur: true,
                        customClass: "tiny-padding",   
                    });
                },
                onMouseLeave: () => {
                    hidePopup(true);
                },
                noButtons: true,
            });
        } else {
            setHasEbike(toggleValue);
            setMake('-');
            setModel('-');
            displayLoading(
                "SAVING E-BIKE SETTINGS",
                async () => {
                    await patchCurrentProfile({settings: { ...findProfile()?.settings, ebike_power_id: null } });
                    setTimeout(() => {
                        setPendingUpdate(!pendingUpdate);
                    }, 1000);
                },
                true
            )
        }
    }

    const handleEbikeSave = async () => {
        displayLoading(
            "SAVING E-BIKE SETTINGS",
            async () => {
                console.log("Saving ebike settings");
                await patchCurrentProfile({
                    settings: {
                        ...findProfile()?.settings,
                        ebike_power_id: 0,
                        customVoltage: voltage,
                        customPower: wattage,
                    }
                });
                setTimeout(() => {
                    setPendingUpdate(!pendingUpdate);
                }, 1000);
            },
            true
        )
    }

    const LightButtons = () => {
        const buttons = [
            { text: "UPLOAD TO LIGHT", onClick: () => handleUploadProfile(), icon: <CloudUploadIcon />, class: device?.properties?.synced ? "" : "radiate-effect" },
            { text: "DELETE PROFILE", onClick: () => handleDeleteProfile(), icon: <DeleteIcon /> },
            { text: "RESET TO DEFAULT", onClick: () => handleResetProfile(), icon: <RestartAltIcon /> },
        ]

        return (
            <Grid container spacing={1}>
                {buttons.map((button, index) => (
                    <Grid size={{ xs: 4 }} key={index}>
                        <Button 
                            key={index} 
                            text={button.text} 
                            onClick={button.onClick} 
                            icon={button.icon}
                            className={`light-button ${button.class ?? ""}`}
                        />
                    </Grid>
                ))}
            </Grid>
        )
    }

    return (
        <>
            {showLoadingScreen ? <div className="center-screen" style={{ width: '30%' }}>
                <div style={{ width: '100%' }}>
                    <div className="status">{status}</div>
                    <Loading isDeterminate={loadingIsDeterminant} progress={uploadProgess} />
                </div>
            </div> :
            <div className="edit-product">
                {device && 
                <div>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 12, lg: 3 }}>
                            <Grid container>
                                <Grid size={{ xs: 5, lg: 12 }}>
                                    <div className="edit-product-img-wrapper">
                                        <Product className={'edit-product-img'} gifMode={'on'} type={device.type} deviceId={device.id} deviceHardwareId={device.hardwareId}/>
                                    </div>
                                    <h2 style={{ marginTop: '10px', fontSize: '22px' }}>{device.type.name?.toLocaleUpperCase()}</h2>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <h3 style={{ marginTop: '5px' }}>
                                            {device.name}
                                        </h3>
                                        <span className="material-symbols-outlined" style={{fontSize: "20px", margin: '5px 0 0 5px', cursor: 'pointer'}} onClick={handleNameChange}>
                                            edit_square
                                        </span>
                                    </div>
                                    <div className="device-info">
                                        <div>
                                            <div>Device firmware</div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', paddingTop: '2px' }}>
                                                {firmwareName ? firmwareName : "Unknown"}
                                                {hasNewFirmware && 
                                                    <ErrorIcon 
                                                        sx={{
                                                            marginLeft: '10px', 
                                                            cursor: 'pointer',
                                                            fontSize: '20px',
                                                            marginBottom: '-4px',
                                                            '&:hover': {
                                                                transform: 'scale(1.2)',
                                                            }
                                                        }} 
                                                        onClick={async () => handleFirmwareUpdate(device!, await deviceManager.checkForNewFirmware(device!, availableFirmware))}
                                                    />}
                                            </div>
                                        </div>
                                        {compatibleEbike(device.type.id) && <>
                                            <Grid container className="info-item">
                                                <Grid size={{xs: 12, xl: 8}}>
                                                    <div>Connecting to <br/> E-Bike?</div>
                                                </Grid>
                                                <Grid size={{xs: 4, xl: 4}}>
                                                    <Toggle
                                                        value={hasEbike}
                                                        setValue={(value: boolean) => handleAddEbike(value)}
                                                        rightLabel="NO"
                                                        leftLabel="YES"
                                                    />
                                                </Grid>   
                                            </Grid>
                                            <div className="info-item">
                                                <div>E-bike motor</div>
                                                <div style={{ fontWeight: '700' }}>{make}</div>
                                            </div>
                                            {model !== 'Other' && <div className="info-item">
                                                <div>Motor model</div>
                                                <div style={{ fontWeight: '700' }}>{model}</div>
                                            </div>}
                                        </>}
                                    </div>
                                </Grid>
                                <Grid size={{ xs: 1, lg: 12 }}></Grid>
                                <Grid size={{ xs: 6, lg: 12 }}>
                                    {hasCobberSettings(device.type.id) &&                                   
                                        <CobberSettings 
                                            settings={findProfile()?.settings ?? {}} 
                                            handleUpdateSettings={handleUpdateSettings}  
                                            type={device.type}
                                        />  
                                    }   
                                    { hasEbike && <Ebike 
                                        handleSave={handleEbikeSave} 
                                        voltage={voltage} 
                                        wattage={wattage} 
                                        isCustom={make === 'Other'}
                                        setVoltage={setVoltage}
                                        setWattage={setWattage}
                                        toggleEbike={() => handleAddEbike(true)}
                                        voltageOptions={voltageOptions}
                                        powerOptions={wattageOptions}
                                    /> }
                                    <Button text="DELETE DEVICE" onClick={handleDelete} />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{ md: 12, lg: 9 }} sx={{padding: width > 1550 ? '0 2%' : '0'}}>
                            <Grid container spacing={5}>
                                <Grid size={{ xs: 4 }}>
                                    <div style={{ marginBottom: '10px', fontSize: '1.2vw' }}>LIGHT PROFILES</div>
                                    <div style={{ marginBottom: '20px', fontSize: '16px', textWrap: 'balance' }} className="knog-font">
                                        Select profile to edit and customise light modes.
                                    </div>
                                </Grid>
                                <Grid size={{ xs: 8 }}>                
                                    <div style={{ marginBottom: '10px', fontSize: '1.2vw' }}>
                                        {findProfile()?.name} LIGHT MODES
                                    </div>
                                    <div style={{ fontSize: '16px' }} className="knog-font">
                                        These are the modes currently assigned to this profile. 
                                        To add/remove patterns simply drag and drop within the profile or from the light mode library below.
                                    </div>
                                </Grid>                              
                            </Grid>
                            <Grid container spacing={5} sx={{mt: 1}}>
                                <Grid size={{ xs: 4 }}>
                                    <Profiles 
                                        profiles={device.profiles} 
                                        currentProfile={currentProfile} 
                                        handleProfileSwitch={handleProfileSwitch} 
                                        handleAddProfile={handleAddProfile} 
                                        handleEditProfile={handleEditProfile}
                                        uploadedProfile={device.properties?.uploadedProfile?.toString() ?? ''}
                                    />
                                </Grid>
                                <Grid size={{ xs: 8 }}>                
                                    <LightButtons />
                                    <DeviceModes 
                                        steadyModes={steadyModesActive}
                                        flashModes={flashModesActive}
                                        handleDrop={handleModeDrop} 
                                        onClick={handleModeClick} 
                                        handleCannotDrop={handleCannotDrop}
                                        deviceType={device.type.id}
                                        lastConfigured={lastConfigured}
                                    />
                                </Grid>                              
                            </Grid>
                            <div className="border-bottom" style={{ marginTop: '20px' }}></div>
                            <div style={{fontSize: '1.2vw', marginTop: '20px', marginLeft: '10px'}}>LIGHT MODE LIBRARY</div>
                            <ModesList 
                                modes={modesAvailable} 
                                handleDrop={handleModeDrop} 
                                deviceType={device.type.id}
                            />
                        </Grid>   
                    </Grid>
                </div>}
            </div>}
        </>
    );
};

export default EditDevice;